"use client";

import { useMemo, useState } from "react";
import { useI18n } from "@/components/i18n-provider";
import dynamic from "next/dynamic";
import Link from "next/link";
import Image from "next/image";
import { useRouter, usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useMarketingUser } from "@/components/marketing-user-provider";
import { trackEvent } from "@/lib/analytics";

const NavUser = dynamic(
  () => import("@/components/nav-user").then((module) => module.NavUser),
  { ssr: false },
);

type HeaderNavItem = {
  id: string;
  label: string;
};

export function Header() {
  const { locale, dictionary } = useI18n();
  const router = useRouter();
  const pathname = usePathname();
  const safePathname = pathname
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const user = useMarketingUser();

  const headerDictionary = dictionary.landing.header;
  const navItems = useMemo(
    () => headerDictionary.nav as HeaderNavItem[],
    [headerDictionary.nav],
  );
  const languageNames = headerDictionary.languageNames as Record<
    string,
    string
  >;

  const otherLocale = locale === "en" ? "id" : "en";
  const otherLocaleName =
    languageNames[otherLocale] ?? otherLocale.toUpperCase();
  const mobileToggleLabel = isMobileMenuOpen
    ? headerDictionary.mobileMenu.close
    : headerDictionary.mobileMenu.open;
  const switchLanguageLabel = headerDictionary.switchLanguageAria.replace(
    "{lang}",
    otherLocaleName,
  );
  const blogHref = `/${locale}/blog`;
  const homeHref = `/${locale}`;
  const blogLabel = "Blog";
  // No `redirect` here on purpose. The header is global chrome with no flow
  // intent, and passing the current path used to override the sign-in page's
  // dashboard default, bouncing every header login back to where it started.
  // Flows that do have a destination (CV upload, checkout, talent pool) pass
  // their own redirect from their own entry point.
  const signInHref = `/${locale}/sign-in`;
  const signUpHref = `/${locale}/sign-up`;

  const handleLocaleToggle = () => {
    const segments = safePathname.split("/");

    if (segments.length > 1) {
      segments[1] = otherLocale;
    } else {
      segments.push(otherLocale);
    }

    const nextPath = segments.join("/") || `/${otherLocale}`;
    // Read the query at click time so the component avoids useSearchParams,
    // which would force this statically-prerendered header into a CSR bailout.
    const queryString =
      typeof window !== "undefined"
        ? window.location.search.replace(/^\?/, "")
        : "";

    router.push(queryString ? `${nextPath}?${queryString}` : nextPath);
  };

  const getSectionHref = (sectionId: string) => `${homeHref}#${sectionId}`;

  return (
    <header className="sticky top-0 z-50 border-b border-border-primary bg-white dark:bg-background">
      <div className="mx-auto flex h-20 items-center justify-between px-4 sm:px-6 lg:px-28">
        <Link href={`/${locale}`} className="flex items-center">
          {/* Served same-origin so the preload reuses the document connection.
              Only the light logo is preloaded: the default theme is light, so the
              dark variant is hidden on first paint. */}
          <Image
            src="/assets/logo.svg"
            alt="Hyred logo"
            width={136}
            height={42}
            priority
            className="h-[42px] w-auto dark:hidden"
          />
          <Image
            src="/assets/dark-logo.svg"
            alt="Hyred logo"
            width={136}
            height={42}
            className="hidden h-[42px] w-auto dark:block"
          />
        </Link>

        <div className="flex items-center gap-3">
          <nav className="hidden items-center gap-3 md:flex">
            {navItems.map((item) => (
              <Link
                key={item.id}
                href={getSectionHref(item.id)}
                className="flex h-11 items-center rounded-lg px-5 text-base font-semibold text-text-heading transition-colors hover:bg-surface-primary"
              >
                {item.label}
              </Link>
            ))}
            <Link
              href={blogHref}
              className="flex h-11 items-center rounded-lg px-5 text-base font-semibold text-text-heading transition-colors hover:bg-surface-primary"
            >
              {blogLabel}
            </Link>
            <button
              type="button"
              onClick={handleLocaleToggle}
              className="flex h-11 items-center rounded-lg px-5 text-base font-semibold text-text-heading transition-colors hover:bg-surface-primary"
              aria-label={switchLanguageLabel}
            >
              {locale.toUpperCase()}
            </button>
          </nav>

          <div className="hidden items-center gap-3 md:flex">
            {!user ? (
              <>
                <Link
                  href={signInHref}
                  onClick={() => trackEvent("sign_in_click", { context: "header" })}
                  className="inline-flex h-11 items-center rounded-lg border border-border-secondary px-[18px] text-base font-semibold text-text-heading shadow-button"
                >
                  {dictionary.auth.signIn}
                </Link>
                <Link
                  href={signUpHref}
                  onClick={() => trackEvent("sign_up_click", { context: "header" })}
                  className="inline-flex h-11 items-center rounded-lg bg-brand-primary px-[18px] text-base font-semibold text-white shadow-button hover:bg-brand-primary-hover"
                >
                  {dictionary.auth.signUp}
                </Link>
              </>
            ) : (
              <NavUser variant="standalone" user={user} />
            )}
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <button
              type="button"
              onClick={handleLocaleToggle}
              aria-label={switchLanguageLabel}
              className="flex h-10 items-center rounded-lg px-3 text-sm font-semibold text-text-heading"
            >
              {locale.toUpperCase()}
            </button>
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={mobileToggleLabel}
            >
              {isMobileMenuOpen ? (
                <X className="h-4 w-4" />
              ) : (
                <Menu className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen ? (
        <div className="md:hidden border-t border-border-primary bg-white dark:bg-background">
          <div className="mx-auto space-y-4 px-4 py-4">
            <nav className="flex flex-col gap-2">
              {navItems.map((item) => (
                <Link
                  key={item.id}
                  href={getSectionHref(item.id)}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="rounded-lg px-3 py-2 text-left text-sm font-semibold text-text-heading hover:bg-surface-primary"
                >
                  {item.label}
                </Link>
              ))}
              <Link
                href={blogHref}
                className="rounded-lg px-3 py-2 text-left text-sm font-semibold text-text-heading hover:bg-surface-primary"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {blogLabel}
              </Link>
            </nav>

            <div className="flex flex-col gap-2 border-t border-border-primary pt-4">
              {!user ? (
                <>
                  <Link
                    href={signInHref}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      trackEvent("sign_in_click", { context: "mobile_header" });
                    }}
                    className="inline-flex h-10 items-center justify-center rounded-lg border border-border-secondary text-sm font-semibold text-text-heading shadow-button"
                  >
                    {dictionary.auth.signIn}
                  </Link>
                  <Link
                    href={signUpHref}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      trackEvent("sign_up_click", { context: "mobile_header" });
                    }}
                    className="inline-flex h-10 items-center justify-center rounded-lg bg-brand-primary text-sm font-semibold text-white shadow-button hover:bg-brand-primary-hover"
                  >
                    {dictionary.auth.signUp}
                  </Link>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <NavUser variant="standalone" user={user} />
                  <span className="text-sm text-text-heading">
                    {headerDictionary.profileLabel}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </header>
  );
}
